import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { FiMail, FiCheck, FiX, FiLoader, FiUpload, FiImage } from 'react-icons/fi';
import SharePointSettings from '../components/SharePointSettings';

export default function SystemSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');

  const categories = [
    { id: 'general', name: 'General', icon: '⚙️' },
    { id: 'security', name: 'Security', icon: '🔒' },
    { id: 'storage', name: 'Storage', icon: '💾' },
    { id: 'financial', name: 'Financial', icon: '💰' },
    { id: 'ai', name: 'AI Settings', icon: '🤖' },
    { id: 'email', name: 'Email', icon: '📧' },
    { id: 'integrations', name: 'Integrations', icon: '🔗' },
    { id: 'advanced', name: 'Advanced', icon: '🔧' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getSettings(true);
      const data = response.data || response;
      setSettings(data.grouped || data || {});
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (settingId, newValue) => {
    try {
      setSaving(true);
      setError(null);
      await adminAPI.updateSetting(settingId, { setting_value: newValue });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Reload settings to get updated values
      await loadSettings();
    } catch (err) {
      console.error('Failed to save setting:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async () => {
    try {
      setSaving(true);
      setError(null);
      // Implementation for bulk save if needed
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <p className="text-green-800 text-sm">Settings saved successfully!</p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Category Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <nav className="space-y-1 p-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition
                    ${activeCategory === category.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-2xl">{category.icon}</span>
                  <span className="font-medium">{category.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {categories.find(c => c.id === activeCategory)?.name} Settings
            </h2>

            {/* Special Logo Upload Component for General Category */}
            {activeCategory === 'general' && (
              <div className="mb-6">
                <LogoUploadPanel onLogoUpdated={loadSettings} />
              </div>
            )}

            {/* Special SMTP Settings Component for Email Category */}
            {activeCategory === 'email' && (
              <SmtpSettingsPanel
                settings={settings[activeCategory] || []}
                onSave={handleSave}
                saving={saving}
              />
            )}

            {/* SharePoint OAuth Configuration Settings */}
            {activeCategory === 'integrations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">SharePoint OAuth Configuration</h3>
                  <div className="space-y-4 mb-8">
                    {settings[activeCategory] && settings[activeCategory].length > 0 ? (
                      settings[activeCategory].map((setting) => (
                        <SettingControl
                          key={setting.id}
                          setting={setting}
                          onSave={handleSave}
                          saving={saving}
                        />
                      ))
                    ) : (
                      <p className="text-gray-500">Loading SharePoint OAuth settings...</p>
                    )}
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">SharePoint Connections</h3>
                  <SharePointSettings />
                </div>
              </div>
            )}

            {/* Regular Settings for Non-Email/Integrations Categories */}
            {activeCategory !== 'email' && activeCategory !== 'integrations' && (
              <div className="space-y-6">
                {settings[activeCategory] && settings[activeCategory].length > 0 ? (
                  settings[activeCategory].map((setting) => (
                    <SettingControl
                      key={setting.id}
                      setting={setting}
                      onSave={handleSave}
                      saving={saving}
                    />
                  ))
                ) : (
                  <p className="text-gray-500">No settings available in this category</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingControl({ setting, onSave, saving }) {
  const [value, setValue] = useState(setting.setting_value);
  const [editing, setEditing] = useState(false);

  const handleSave = () => {
    onSave(setting.id, value);
    setEditing(false);
  };

  const handleCancel = () => {
    setValue(setting.setting_value);
    setEditing(false);
  };

  const renderInput = () => {
    // Determine input type based on value type
    const valueType = typeof setting.setting_value;

    if (valueType === 'boolean') {
      return (
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => setValue(e.target.checked)}
            disabled={!editing}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-700">
            {value ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }

    if (valueType === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          disabled={!editing}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
        />
      );
    }

    if (valueType === 'string') {
      if (setting.is_sensitive) {
        return (
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!editing}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
          />
        );
      }

      return (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!editing}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
        />
      );
    }

    // For objects/arrays, use textarea with JSON
    return (
      <textarea
        value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try {
            setValue(JSON.parse(e.target.value));
          } catch {
            setValue(e.target.value);
          }
        }}
        disabled={!editing}
        rows={4}
        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 font-mono text-sm"
      />
    );
  };

  return (
    <div className="border-b border-gray-200 pb-6 last:border-b-0">
      <div className="flex justify-between items-start mb-2">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            {setting.setting_name || setting.setting_key}
          </label>
          {setting.description && (
            <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
          )}
        </div>
        <div className="flex space-x-2">
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-2">
        {renderInput()}
      </div>

      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
        <span>Key: {setting.setting_key}</span>
        {setting.is_sensitive && (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
            Sensitive
          </span>
        )}
        {setting.updated_at && (
          <span>Last updated: {new Date(setting.updated_at).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );
}

// Logo Upload Panel Component
function LogoUploadPanel({ onLogoUpdated }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const response = await adminAPI.getCompanyLogo();
      if (response.data?.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      console.error('Failed to load logo:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setUploadResult({
        success: false,
        message: 'Only image files (JPEG, PNG, GIF, SVG) are allowed'
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadResult({
        success: false,
        message: 'File size must be less than 5MB'
      });
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewUrl(event.target.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await adminAPI.uploadLogo(formData);

      setUploadResult({
        success: true,
        message: 'Logo uploaded successfully!'
      });

      setLogoUrl(response.data.logo_url);
      setPreviewUrl(null);

      // Reload settings to update the list
      if (onLogoUpdated) {
        onLogoUpdated();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setUploadResult(null), 3000);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to upload logo'
      });
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-b border-gray-200 pb-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FiImage className="mr-2" />
            Company Logo
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Upload your company logo. This will be displayed on the login screen and sidebar.
          </p>
        </div>
      </div>

      {/* Upload Result Message */}
      {uploadResult && (
        <div className={`mb-4 rounded-lg p-4 ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            {uploadResult.success ? (
              <FiCheck className="h-5 w-5 text-green-600 mr-3" />
            ) : (
              <FiX className="h-5 w-5 text-red-600 mr-3" />
            )}
            <p className={`text-sm ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
              {uploadResult.message}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Current Logo Display */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Logo
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 h-48 flex items-center justify-center bg-gray-50">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : (
              <div className="text-center text-gray-400">
                <FiImage className="mx-auto h-12 w-12 mb-2" />
                <p className="text-sm">No logo uploaded</p>
              </div>
            )}
            <div style={{ display: 'none' }} className="text-center text-gray-400">
              <FiImage className="mx-auto h-12 w-12 mb-2" />
              <p className="text-sm">Logo not found</p>
            </div>
          </div>
        </div>

        {/* Upload New Logo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload New Logo
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 h-48 flex items-center justify-center bg-gray-50">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Logo Preview"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="text-center">
                <FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <FiLoader className="animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FiUpload className="mr-2" />
                      Choose File
                    </>
                  )}
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                />
                <p className="text-xs text-gray-500 mt-2">
                  PNG, JPG, GIF, SVG up to 5MB
                </p>
              </div>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center text-sm text-blue-600">
              <FiLoader className="animate-spin mr-2" />
              Uploading logo...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// SMTP Settings Panel Component with Test Functionality
function SmtpSettingsPanel({ settings, onSave, saving }) {
  const [smtpConfig, setSmtpConfig] = useState({});
  const [testing, setTesting] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState(null);

  useEffect(() => {
    // Initialize config from settings
    console.log('Loading SMTP settings:', settings);
    const config = {};
    settings.forEach(setting => {
      config[setting.setting_key] = setting.setting_value;
      config[`${setting.setting_key}_id`] = setting.id;
    });
    console.log('Initialized SMTP config:', config);
    setSmtpConfig(config);
    setHasChanges(false); // Reset changes when settings reload
  }, [settings]);

  const handleChange = (key, value) => {
    setSmtpConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await adminAPI.testSmtp({
        smtp_host: smtpConfig.smtp_host || '',
        smtp_port: parseInt(smtpConfig.smtp_port) || 587,
        smtp_secure: smtpConfig.smtp_secure || false,
        smtp_tls_reject_unauthorized: smtpConfig.smtp_tls_reject_unauthorized !== false,
        smtp_username: smtpConfig.smtp_username || '',
        smtp_password: smtpConfig.smtp_password || '',
      });

      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const loadQueueStatus = async () => {
    setLoadingQueue(true);
    try {
      const [queueResponse, statusResponse] = await Promise.all([
        adminAPI.getEmailQueueStatus(),
        adminAPI.getSmtpStatus()
      ]);
      setQueueStatus(queueResponse.data);
      setSmtpStatus(statusResponse.data);
    } catch (error) {
      console.error('Failed to load queue status:', error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const handleProcessQueue = async () => {
    setLoadingQueue(true);
    try {
      const response = await adminAPI.processEmailQueue();
      setQueueStatus(response.data);
      setTestResult({
        success: true,
        message: 'Queue processing triggered. Check status below.'
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message
      });
    } finally {
      setLoadingQueue(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setTestResult({
        success: false,
        message: 'Please enter an email address to send test email'
      });
      return;
    }

    setSendingTestEmail(true);
    setTestResult(null);

    try {
      const response = await adminAPI.testEmail(testEmail);
      
      // Update queue status from response
      if (response.data?.queueStatus) {
        setQueueStatus(response.data.queueStatus);
      }
      
      setTestResult({
        success: true,
        message: response.data?.message || `Test email sent to ${testEmail}. Check your inbox and spam folder.`
      });
      
      // Refresh queue status after a moment
      setTimeout(() => {
        loadQueueStatus();
      }, 1000);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    setTestResult(null);
    
    try {
      const savePromises = settings.map(setting => {
        const newValue = smtpConfig[setting.setting_key];
        // Skip if value is undefined or hasn't changed
        if (newValue === undefined) return Promise.resolve();
        
        // Convert values to proper types for comparison
        let oldValue = setting.setting_value;
        let compareNew = newValue;
        
        // Handle different data types
        if (typeof oldValue === 'boolean' || typeof compareNew === 'boolean') {
          oldValue = Boolean(oldValue);
          compareNew = Boolean(compareNew);
        } else if (typeof oldValue === 'number' || !isNaN(Number(compareNew))) {
          oldValue = Number(oldValue);
          compareNew = Number(compareNew);
        }
        
        // Only save if value actually changed
        if (compareNew !== oldValue) {
          console.log(`Saving ${setting.setting_key}: ${oldValue} -> ${compareNew}`);
          return onSave(setting.id, newValue);
        }
        return Promise.resolve();
      });

      await Promise.all(savePromises);
      setHasChanges(false);
      
      // Show success message
      setTestResult({ success: true, message: 'Settings saved successfully!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setTestResult({ 
        success: false, 
        message: 'Failed to save settings: ' + (error.response?.data?.message || error.message) 
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Banner */}
      {testResult && (
        <div className={`rounded-lg p-4 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            {testResult.success ? (
              <FiCheck className="h-5 w-5 text-green-600 mr-3" />
            ) : (
              <FiX className="h-5 w-5 text-red-600 mr-3" />
            )}
            <div>
              <h4 className={`font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                {testResult.success ? 'SMTP Connection Successful' : 'SMTP Connection Failed'}
              </h4>
              <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Enable Email Notifications Toggle */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-blue-900">Email Notifications</h3>
            <p className="text-sm text-blue-700 mt-1">
              Enable automated email notifications for actions, assignments, and password resets
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={smtpConfig.enable_email_notifications || false}
              onChange={(e) => handleChange('enable_email_notifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <FiMail className="mr-2" />
          SMTP Server Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* SMTP Host */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMTP Host *
            </label>
            <input
              type="text"
              value={smtpConfig.smtp_host || ''}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">SMTP server hostname or IP address</p>
          </div>

          {/* SMTP Port */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMTP Port *
            </label>
            <input
              type="number"
              value={smtpConfig.smtp_port || 587}
              onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Common: 587 (TLS), 465 (SSL), 25 (unencrypted)</p>
          </div>

          {/* SMTP Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMTP Username
            </label>
            <input
              type="text"
              value={smtpConfig.smtp_username || ''}
              onChange={(e) => handleChange('smtp_username', e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* SMTP Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMTP Password
            </label>
            <input
              type="password"
              value={smtpConfig.smtp_password || ''}
              onChange={(e) => handleChange('smtp_password', e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* From Email */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Email Address *
            </label>
            <input
              type="email"
              value={smtpConfig.email_from || ''}
              onChange={(e) => handleChange('email_from', e.target.value)}
              placeholder="noreply@cimchemicals.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Email address that will appear as the sender</p>
          </div>
        </div>

        {/* SSL/TLS Settings */}
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <h4 className="font-medium text-gray-900">Security Settings</h4>

          {/* Use SSL/TLS Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="font-medium text-gray-900">Use SSL/TLS</label>
              <p className="text-sm text-gray-600">Enable secure connection (use true for port 465)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={smtpConfig.smtp_secure || false}
                onChange={(e) => handleChange('smtp_secure', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Verify SSL Certificate Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="font-medium text-gray-900">Verify SSL Certificates</label>
              <p className="text-sm text-gray-600">Disable only for self-signed certificates (not recommended for production)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={smtpConfig.smtp_tls_reject_unauthorized !== false}
                onChange={(e) => handleChange('smtp_tls_reject_unauthorized', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleTestConnection}
              disabled={testing || !smtpConfig.smtp_host}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {testing ? (
                <>
                  <FiLoader className="animate-spin mr-2" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </button>

            <button
              onClick={handleSaveAll}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSaving ? (
                <>
                  <FiLoader className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>

            {hasChanges && !isSaving && (
              <span className="text-sm text-amber-600 flex items-center">
                <span className="w-2 h-2 bg-amber-600 rounded-full mr-2"></span>
                Unsaved changes
              </span>
            )}
          </div>

          {/* Send Test Email */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2 flex items-center">
              <FiMail className="mr-2" />
              Send Test Email
            </h4>
            <p className="text-sm text-blue-700 mb-3">
              Send a test action notification email to verify your SMTP configuration and email delivery.
            </p>
            <div className="flex items-center space-x-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail || !testEmail || !smtpConfig.enable_email_notifications}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
              >
                {sendingTestEmail ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiMail className="mr-2" />
                    Send Test
                  </>
                )}
              </button>
            </div>
            {!smtpConfig.enable_email_notifications && (
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ Email notifications are disabled. Enable them above to send test emails.
              </p>
            )}
          </div>
        </div>

        {/* Email Queue Status */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-gray-900">Email Queue Status</h5>
            <div className="flex space-x-2">
              <button
                onClick={loadQueueStatus}
                disabled={loadingQueue}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 flex items-center"
              >
                {loadingQueue ? (
                  <>
                    <FiLoader className="animate-spin mr-1" />
                    Loading...
                  </>
                ) : (
                  'Refresh'
                )}
              </button>
              <button
                onClick={handleProcessQueue}
                disabled={loadingQueue}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                Process Queue
              </button>
            </div>
          </div>

          {queueStatus ? (
            <div className="space-y-3">
              {/* SMTP Status Alert */}
              {smtpStatus && (
                <div className={`p-3 rounded-lg border ${
                  smtpStatus.configured && smtpStatus.enabled && smtpStatus.hasUsername && smtpStatus.hasPassword
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <h6 className="font-medium text-sm mb-2">
                    {smtpStatus.configured && smtpStatus.enabled && smtpStatus.hasUsername && smtpStatus.hasPassword
                      ? '✅ SMTP Configuration OK'
                      : '⚠️ SMTP Configuration Issue'}
                  </h6>
                  <div className="text-xs space-y-1">
                    <div>Enabled: <strong>{smtpStatus.enabled ? 'Yes' : 'No'}</strong></div>
                    <div>Host: <strong>{smtpStatus.host || 'Not set'}</strong></div>
                    <div>Port: <strong>{smtpStatus.port || 'Not set'}</strong></div>
                    <div>Secure (SSL/TLS): <strong>{smtpStatus.secure ? 'Yes' : 'No'}</strong></div>
                    <div>From Address: <strong>{smtpStatus.from || 'Not set'}</strong></div>
                    <div>Username: <strong>{smtpStatus.hasUsername ? 'Set' : 'Not set'}</strong></div>
                    <div>Password: <strong>{smtpStatus.hasPassword ? 'Set' : 'Not set'}</strong></div>
                  </div>
                  {(!smtpStatus.enabled || !smtpStatus.hasUsername || !smtpStatus.hasPassword) && (
                    <p className="text-xs text-red-600 mt-2">
                      ⚠️ Fix the configuration above and save settings before sending emails.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-4 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <div className="text-gray-500 text-xs">Pending</div>
                  <div className="text-2xl font-bold text-blue-600">{queueStatus.queueSize}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-gray-500 text-xs">Processing</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {queueStatus.processing ? 'Yes' : 'No'}
                  </div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-gray-500 text-xs">Completed</div>
                  <div className="text-2xl font-bold text-green-600">{queueStatus.completedCount}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-gray-500 text-xs">Failed</div>
                  <div className="text-2xl font-bold text-red-600">{queueStatus.failedCount}</div>
                </div>
              </div>

              {queueStatus.jobs && queueStatus.jobs.length > 0 && (
                <div>
                  <h6 className="text-sm font-medium text-gray-700 mb-2">Pending Jobs:</h6>
                  <div className="space-y-1">
                    {queueStatus.jobs.map((job) => (
                      <div key={job.id} className="text-xs bg-white p-2 rounded border flex justify-between">
                        <span><strong>{job.type}</strong> (Attempt {job.attempts + 1}/3)</span>
                        <span className="text-gray-500">{new Date(job.addedAt).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {queueStatus.recentFailed && queueStatus.recentFailed.length > 0 && (
                <div>
                  <h6 className="text-sm font-medium text-red-700 mb-2">Recent Failures:</h6>
                  <div className="space-y-1">
                    {queueStatus.recentFailed.map((job) => (
                      <div key={job.id} className="text-xs bg-red-50 p-2 rounded border border-red-200">
                        <div className="flex justify-between mb-1">
                          <span><strong>{job.type}</strong></span>
                          <span className="text-gray-500">{new Date(job.failedAt).toLocaleString()}</span>
                        </div>
                        <div className="text-red-600">{job.lastError}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {queueStatus.recentCompleted && queueStatus.recentCompleted.length > 0 && (
                <div>
                  <h6 className="text-sm font-medium text-green-700 mb-2">Recent Completions:</h6>
                  <div className="space-y-1">
                    {queueStatus.recentCompleted.slice(0, 5).map((job) => (
                      <div key={job.id} className="text-xs bg-green-50 p-2 rounded border border-green-200 flex justify-between">
                        <span><strong>{job.type}</strong></span>
                        <span className="text-gray-500">{new Date(job.completedAt).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <button
                onClick={loadQueueStatus}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Load Queue Status
              </button>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
          <h5 className="font-medium text-gray-900 mb-2">Common SMTP Providers</h5>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>Gmail:</strong> smtp.gmail.com:587 (TLS) or smtp.gmail.com:465 (SSL)</li>
            <li>• <strong>Outlook:</strong> smtp-mail.outlook.com:587</li>
            <li>• <strong>SendGrid:</strong> smtp.sendgrid.net:587</li>
            <li>• <strong>Mailgun:</strong> smtp.mailgun.org:587</li>
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            Note: Some providers (like Gmail) may require app-specific passwords or OAuth2 authentication.
          </p>
        </div>
      </div>
    </div>
  );
}
